/* Mother's Day Collection — section behaviors
   - Scroll-reveal fade-in (with stagger), respects prefers-reduced-motion
   - Story section replays its reveal each time it re-enters the viewport
   - Pizza feature card: radio drives image, tag, price, and the variant added to cart
   - Add-to-cart wiring: POST /cart/add.js for cards with a variant id; success opens
     the cart drawer if present, otherwise shows an inline toast on the card
   - Smooth-scroll for #shop-by-collection anchors
   - Floating pills: auto-show when section is in viewport, hide otherwise
*/

(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.mdInit === '1') return;
    root.dataset.mdInit = '1';

    var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    initReveal(root, prefersReduced);
    initStoryReplay(root, prefersReduced);
    initPizzaCard(root);
    initAddToCart(root);
    initSmoothScroll(root);
    initFloatingPills(root);
  }

  /* ───── Scroll-reveal ───── */
  function initReveal(root, prefersReduced) {
    var els = root.querySelectorAll('.md-reveal');
    if (!els.length) return;

    if (prefersReduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }

    // Skip story-section reveals — those are owned by the replay observer
    var storyEl = root.querySelector('.md-story');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var parent = entry.target.parentElement;
          var idx = parent ? Array.from(parent.children).indexOf(entry.target) : 0;
          setTimeout(function () { entry.target.classList.add('in'); }, Math.max(0, idx) * 90);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function (el) {
      if (storyEl && storyEl.contains(el)) return;
      io.observe(el);
    });
  }

  /* ───── Story re-play observer ───── */
  function initStoryReplay(root, prefersReduced) {
    var story = root.querySelector('.md-story');
    if (!story) return;
    var reveals = story.querySelectorAll('.md-reveal');
    if (!reveals.length) return;

    if (prefersReduced || !('IntersectionObserver' in window)) {
      reveals.forEach(function (el) { el.classList.add('in'); });
      return;
    }

    reveals.forEach(function (el) { el.classList.remove('in'); });

    var replay = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var target = entry.target;
        if (entry.isIntersecting) {
          var parent = target.parentElement;
          var idx = parent ? Array.from(parent.children).indexOf(target) : 0;
          setTimeout(function () { target.classList.add('in'); }, Math.max(0, idx) * 120);
        } else {
          target.classList.remove('in');
        }
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -60px 0px' });

    reveals.forEach(function (el) { replay.observe(el); });
  }

  /* ───── Pizza feature card: radio drives image, tag, price, variant ───── */
  function initPizzaCard(root) {
    var cards = root.querySelectorAll('.md-card.md-pizza');
    cards.forEach(function (card) {
      var radios = card.querySelectorAll('input[type="radio"]');
      if (!radios.length) return;
      var priceEl = card.querySelector('.md-basket-cta .md-basket-price');
      var ctaEl = card.querySelector('.md-basket-cta');

      function sync() {
        var checked = card.querySelector('input[type="radio"]:checked');
        if (!checked) return;
        var price = checked.getAttribute('data-price');
        var variantId = checked.getAttribute('data-variant-id');
        var isB = checked.getAttribute('data-option') === 'b';
        if (priceEl && price) priceEl.textContent = price;
        if (ctaEl && variantId) ctaEl.setAttribute('data-variant-id', variantId);
        card.classList.toggle('show-b', isB);
        card.classList.toggle('show-a', !isB);
      }

      radios.forEach(function (r) {
        r.addEventListener('change', sync);
      });
      sync();
    });
  }

  /* ───── Add to cart wiring ───── */
  function initAddToCart(root) {
    var buttons = root.querySelectorAll('[data-md-cart-add]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return; // No variant — let any default link behavior run
        e.preventDefault();
        addToCart(btn, variantId);
      });
    });
  }

  function addToCart(btn, variantId) {
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');

    var body = { items: [{ id: Number(variantId), quantity: 1 }] };

    var sectionsParam = collectCartSectionIds();
    if (sectionsParam) body.sections = sectionsParam;

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw json;
          return json;
        });
      })
      .then(function (json) {
        btn.classList.remove('is-loading');
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function' && json.sections) {
          var fakeParsed = { sections: json.sections, items: [json] };
          try {
            drawer.renderContents(fakeParsed);
            return;
          } catch (err) { /* fall through */ }
        }
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          return;
        }
        // No drawer — show toast on the card
        showToast(btn, 'Added to basket', false);
      })
      .catch(function (err) {
        btn.classList.remove('is-loading');
        var msg = (err && err.description) || 'Could not add to basket';
        showToast(btn, msg, true);
      });
  }

  function collectCartSectionIds() {
    var ids = [];
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.id) ids.push(drawer.id);
    var bubble = document.querySelector('#cart-icon-bubble');
    if (bubble) ids.push('cart-icon-bubble');
    var notif = document.querySelector('cart-notification');
    if (notif && notif.id) ids.push(notif.id);
    return ids.join(',');
  }

  function showToast(btn, message, isError) {
    var card = btn.closest('.md-card');
    if (!card) return;
    var toast = card.querySelector('.md-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'md-toast';
      card.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.toggle('is-error', !!isError);
    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });
    setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2400);
  }

  /* ───── Smooth scroll for in-page anchors ───── */
  function initSmoothScroll(root) {
    var links = root.querySelectorAll('a[href^="#"]');
    var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var hash = link.getAttribute('href');
        if (!hash || hash === '#' || hash.length < 2) return;
        var target = document.querySelector(hash);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({
          behavior: prefersReduced ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    });
  }

  /* ───── Floating sticky pills — show only when section is in viewport ───── */
  function initFloatingPills(root) {
    var pills = root.querySelector('.mothers-day-floating');
    if (!pills) return;

    if (!('IntersectionObserver' in window)) {
      pills.classList.add('is-visible');
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        pills.classList.toggle('is-visible', entry.isIntersecting);
      });
    }, { threshold: 0.05 });

    io.observe(root);
  }

  /* ───── Boot ───── */
  function boot() {
    document.querySelectorAll('.mothers-day').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-init on theme editor section reload
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) e.target.querySelectorAll('.mothers-day').forEach(init);
  });
})();
